import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { doc, getDoc, collection, query, where, getDocs, setDoc } from 'firebase/firestore';
import { updatePassword, updateProfile, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import DashboardLayout from '../dashboard/_components/DashboardLayout';
import { 
  Settings as SettingsIcon, User, Lock, Bell, 
  Shield, Eye, EyeOff, Save, Send, Database
} from 'lucide-react';
import { createTestNotification, testNotificationSystem } from '../../utils/notifications';
import { toast } from 'sonner';

export default function Settings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Form states
  const [profileData, setProfileData] = useState({
    fullName: 'Admin User',
    email: 'admin@admin.com',
    phone: '+91 9876543210',
    role: 'Super Admin',
    avatar: null
  });

  const [securityData, setSecurityData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [testingNotification, setTestingNotification] = useState(false);

  // Test notification handlers
  const handleTestNotification = async () => {
    setTestingNotification(true);
    try {
      await createTestNotification();
      toast.success('Test notification created successfully! Check the notification bell icon in the header.');
    } catch (error) {
      // Silent fail
      toast.error('Failed to create test notification: ' + error.message);
    } finally {
      setTestingNotification(false);
    }
  };

  const handleFullNotificationTest = async () => {
    setTestingNotification(true);
    try {
      const result = await testNotificationSystem();
      toast.success(`Test completed! Created notification ID: ${result.notificationId}. Total notifications: ${result.totalNotifications}. Check console for details.`);
    } catch (error) {
      // Silent fail
      toast.error('Test failed: ' + error.message);
    } finally {
      setTestingNotification(false);
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((u) => {
      if (u) {
        setUser(u);
        // Always reflect auth email immediately
        setProfileData(prev => ({
          ...prev,
          email: u.email || prev.email,
          fullName: u.displayName || prev.fullName,
        }));

        // Fetch profile from Firestore super-admin collection
        const fetchProfile = async () => {
          setLoadingProfile(true);
          try {
            // Try by UID first
            let snap = await getDoc(doc(db, 'super-admin', u.uid));
            if (!snap.exists()) {
              // Fallback: query by email
              const q = query(collection(db, 'super-admin'), where('email', '==', u.email || ''));
              const qSnap = await getDocs(q);
              if (!qSnap.empty) {
                snap = qSnap.docs[0];
              } else {
                snap = null;
              }
            }

            if (snap) {
              const data = snap.data() || {};
              setProfileData(prev => ({
                ...prev,
                fullName: data.fullName || data.name || prev.fullName,
                email: u.email || data.email || prev.email,
                phone: data.phoneNumber || data.phone || prev.phone,
                role: data.role || prev.role,
                avatar: data.avatarUrl || data.avatar || prev.avatar,
              }));
            }
          } catch (e) {
            // Silent fail - keep defaults
          } finally {
            setLoadingProfile(false);
          }
        };

        fetchProfile();
      } else {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      // Update Firebase Auth profile
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: profileData.fullName,
          photoURL: profileData.avatar
        });

        // Update Firestore super-admin document
        const userDocRef = doc(db, 'super-admin', auth.currentUser.uid);
        await setDoc(userDocRef, {
          fullName: profileData.fullName,
          name: profileData.fullName,
          email: profileData.email,
          phoneNumber: profileData.phone,
          phone: profileData.phone,
          avatarUrl: profileData.avatar,
          avatar: profileData.avatar,
          role: profileData.role,
          updatedAt: new Date()
        }, { merge: true });

        toast.success('Profile updated successfully!');
      }
    } catch (error) {
      // Silent fail
      toast.error('Failed to update profile: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    // Validation
    if (!securityData.currentPassword) {
      toast.warning('Please enter your current password');
      return;
    }
    if (!securityData.newPassword) {
      toast.warning('Please enter a new password');
      return;
    }
    if (securityData.newPassword.length < 6) {
      toast.warning('New password must be at least 6 characters long');
      return;
    }
    if (securityData.newPassword !== securityData.confirmPassword) {
      toast.warning('New passwords do not match!');
      return;
    }

    setSaving(true);
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new Error('No user logged in');
      }

      // Re-authenticate user before changing password
      const credential = EmailAuthProvider.credential(
        user.email,
        securityData.currentPassword
      );
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, securityData.newPassword);

      // Clear form
      setSecurityData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password changed successfully!');
    } catch (error) {
      // Silent fail
      if (error.code === 'auth/wrong-password') {
        toast.error('Current password is incorrect');
      } else if (error.code === 'auth/weak-password') {
        toast.error('New password is too weak');
      } else {
        toast.error('Failed to change password: ' + error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const tabs = [
    { id: 'profile', name: 'Profile', icon: User },
    { id: 'security', name: 'Security', icon: Lock },
    { id: 'notifications', name: 'Notifications', icon: Bell }
  ];

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
              <div className="p-2 bg-purple-600 rounded-xl">
                <SettingsIcon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              Settings
            </h1>
            <p className="text-gray-500 text-sm mt-2 flex items-center gap-2">
              <span>Manage your account settings and preferences</span>
              {loadingProfile && (
                <span className="text-xs text-gray-400">Loading profile…</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
        <div className="flex overflow-x-auto scrollbar-hide border-b border-gray-100">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-6">Profile Information</h2>
          
          {/* Avatar Display */}
          <div className="mb-6 pb-6 border-b border-gray-100">
            <label className="block text-sm font-medium text-gray-700 mb-3">Profile Photo</label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center overflow-hidden">
                {profileData.avatar ? (
                  <img src={profileData.avatar} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-10 h-10 text-blue-600" />
                )}
              </div>
            </div>
          </div>

          {/* Profile Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
              <input
                type="text"
                value={profileData.fullName}
                onChange={(e) => setProfileData({ ...profileData, fullName: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <input
                type="tel"
                value={profileData.phone}
                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <input
                type="text"
                value={profileData.role}
                disabled
                className="w-full px-4 py-2.5 rounded-xl bg-gray-100 border border-gray-200 text-gray-600 cursor-not-allowed"
              />
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={handleSaveProfile}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          {/* Change Password */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-6">Change Password</h2>
            
            <div className="space-y-4 max-w-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    // value={securityData.currentPassword}
                    onChange={(e) => setSecurityData({ ...securityData, currentPassword: e.target.value })}
                    className="w-full px-4 py-2.5 pr-12 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={securityData.newPassword}
                    onChange={(e) => setSecurityData({ ...securityData, newPassword: e.target.value })}
                    className="w-full px-4 py-2.5 pr-12 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                <input
                  type="password"
                  value={securityData.confirmPassword}
                  onChange={(e) => setSecurityData({ ...securityData, confirmPassword: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <button
                onClick={handleChangePassword}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Lock className="w-4 h-4" />
                {saving ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </div>

          {/* Active Sessions */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Active Sessions</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Shield className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Current Session</p>
                    <p className="text-xs text-gray-500">Windows • Chrome • India</p>
                  </div>
                </div>
                <span className="text-xs text-green-600 font-medium">Active Now</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-6">Notification Preferences</h2>

          {/* Test Notification Section */}
          <div className="mb-8 pb-6 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">🧪 Test Notification System</h3>
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <p className="text-sm text-blue-900 mb-4">
                Test the notification system by creating sample notifications. 
                They will appear in the notification bell icon in the header.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleTestNotification}
                  disabled={testingNotification}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  <Send className="w-4 h-4" />
                  {testingNotification ? 'Creating...' : 'Create Test Notification'}
                </button>
                <button
                  onClick={handleFullNotificationTest}
                  disabled={testingNotification}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  <Database className="w-4 h-4" />
                  {testingNotification ? 'Testing...' : 'Run Full System Test'}
                </button>
              </div>
              <p className="text-xs text-blue-700 mt-3">
                💡 Tip: Open the browser console (F12) to see detailed logs when running the full system test.
              </p>
            </div>
          </div>


        </div>
      )}
    </DashboardLayout>
  );
}
